/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useState } from "react";
import { AccountContext, CaptivePortalContext, ContextUpdater } from "../../../context/ContextProvider";
import { Button, Input, Label } from "reactstrap";
import { requestDeleteCaptivePortalMedia, requestUpdateCaptivePortalMedia } from "../../../context/RequestManager";
import CenterModal from "../../../elements/CenterModal";
import ModalBoolean from "../../../elements/ModalBoolean";
import ScrollableDiv from "../../../elements/ScrollableDiv";
import GridContainer from "../../../elements/GridContainer";
import ActionButtonGroup from "../../../elements/ActionButtonGroup";
import ActionButton from "../../../elements/ActionButton";
import Media from "../../../elements/Media";
import ModalImageUploadForm from "./ModalImageUploadForm";
import useUpdateState from "../../../util/useUpdateState";

const ModalImageManager = ({
  isOpen,
  onUploaded,
  onClose,
  scope,
  type,
}) => {
  // global context
  const contextUpdater = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);
  const captivePortalContext = useContext(CaptivePortalContext);
  const imageList = captivePortalContext.imageList.filter(image => type == null || image.type === type);

  // state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editState, , updateEditState] = useUpdateState({ isOpen: false, id: null, name: null });
  const [deleteState, , updateDeleteState] = useUpdateState({ isOpen: false, id: null, name: null });
  const readOnly = accountContext.permissions?.readOnly ?? false;

  // handlers
  const handleUpload = (newImage) => {
    onUploaded?.(newImage);
    setUploadOpen(false);
  };

  const handleUpdate = (image) => {
    updateEditState({ isOpen: true, id: image.id, name: image.name });
  };

  const handleConfirmUpdate = () => {
    if (readOnly) { 
      updateEditState({ isOpen: false });
      return;
    }

    requestUpdateCaptivePortalMedia({
      context: contextUpdater,
      token: accountContext.token,
      id: editState.id,
      name: editState.name,
      clientId: scope,
      completion: () => updateEditState({ isOpen: false })
    });
  };

  const handleDelete = (image) => {
    updateDeleteState({ isOpen: true, id: image.id, name: image.name });
  };

  const handleConfirmDelete = () => {
    if (readOnly) { 
      updateEditState({ isOpen: false });
      return;
    }
    
    requestDeleteCaptivePortalMedia({
      context: contextUpdater,
      token: accountContext.token,
      id: deleteState.id,
      clientId: scope,
      completion: () => updateDeleteState({ isOpen: false })
    });
  };

  return (
    <CenterModal isOpen={isOpen} toggle={() => {}} size="lg">
      <div className="d-flex justify-content-between">
        <div style={{ width: "28px" }} />
        <Label className="text-center" tag="h3">Manage Images</Label>
        <ActionButton type="close" onClick={onClose} />
      </div>
      <div className="w-100 d-flex" style={{ height: "500px" }}>
        <ScrollableDiv>
          <GridContainer width={180}>
            {imageList.map((image, index) => (
              <div className="p-2" align="center" key={index}>
                <Label className="no-spacing" tag="h6">{image.name}</Label>
                <Media
                  src={image.url}
                  style={{
                    width: "120px",
                    height: "120px",
                    border: "1px solid #8888",
                    borderRadius: "8px"
                  }}
                />
                <ActionButtonGroup
                  className="py-1 d-flex justify-content-center"
                  editHandler={() => handleUpdate(image)}
                  deleteHandler={() => handleDelete(image)}
                />
              </div>
            ))}
          </GridContainer>
        </ScrollableDiv>
      </div>
      <div align="center">
        <Button size="lg" color="info" onClick={() => setUploadOpen(true)}>
          Upload Image <i className="ps-1 bi-cloud-upload" />
        </Button>
      </div>
      <ModalImageUploadForm
        type={type}
        isOpen={uploadOpen}
        toggle={() => setUploadOpen(false)}
        onUploaded={handleUpload}
      />
      <CenterModal isOpen={editState.isOpen} toggle={() => updateEditState({ isOpen: false })}>
        <Label className="pb-2 text-center" tag="h3">Update Image</Label>
        <Label className="no-spacing">Image Name</Label>
        <Input value={editState.name} onChange={(e) => updateEditState({ name: e.target.value })} />
        <div className="pt-2 d-flex justify-content-between">
          <Button onClick={() => updateEditState({ isOpen: false })}>Dismiss</Button>
          <Button color="success" onClick={handleConfirmUpdate}>Update</Button>
        </div>
      </CenterModal>
      <ModalBoolean
        isOpen={deleteState.isOpen}
        toggle={() => updateDeleteState({ isOpen: false })}
        title="Confirm Delete"
        rightText="Delete"
        rightColor="danger"
        rightSelected={handleConfirmDelete}
      >
        <div className="d-flex flex-column align-items-center">
          <Label>
            Are you sure you want to delete image
            <span className="fw-bold"> {deleteState.name}</span>?
          </Label>
          <Label className="mt-2 fw-bold">This action cannot be undone</Label>
        </div>
      </ModalBoolean>
    </CenterModal>
  )
};

export default ModalImageManager;
